import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "../api";
import { Copy, Gift, Instagram } from "lucide-react";
import { mediaUrl } from "../media";

const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[^a-z0-9]/g, "");
const isImg = (u) => !!u && !/instagram\.com/i.test(u);
const words = (s) => (s || "").split(/\s+/).map(norm).filter(w => w.length >= 5);
const similar = (a, b) => {
  const na = norm(a), nb = norm(b);
  if (na.includes(nb) || nb.includes(na)) return true;
  return words(a).some(w => nb.includes(w.slice(0, 6))) || words(b).some(w => na.includes(w.slice(0, 6)));
};

export default function PartnershipsPage() {
  const [items, setItems] = useState([]);
  const [params, setParams] = useSearchParams();
  const active = params.get("categoria") || "";
  useEffect(() => { api.get("/partnerships").then(r=> setItems(r.data)); }, []);
  const [companies, setCompanies] = useState([]);
  useEffect(() => { api.get("/business-partners/public").then(r=> setCompanies(r.data)).catch(()=>{}); }, []);

  const counts = items.reduce((acc, p) => { const c = p.category || "Outros"; acc[c] = (acc[c] || 0) + 1; return acc; }, {});
  const shown = active ? items.filter(p => (p.category || "Outros") === active) : items;
  const pick = (c) => setParams(c ? { categoria: c } : {});

  const photoOf = (p) => {
    if (isImg(p.image_url)) return p.image_url;
    const withImg = companies.filter(c => c.image_url);
    const linked = withImg.find(c => c.business_partner_id === p.business_partner_id);
    if (linked) return linked.image_url;
    const c = withImg.find(c => similar(c.name, p.name));
    return c?.image_url || "";
  };

  const copy = (code) => { navigator.clipboard.writeText(code); toast.success("Cupom copiado!"); };

  return (
    <div className="max-w-6xl mx-auto px-6 py-14">
      <div className="text-center mb-10">
        <div className="tag-sun mb-3 inline-block">PARCERIAS</div>
        <h1 className="font-serif text-4xl sm:text-5xl">Benefícios exclusivos</h1>
        <p className="text-[color:var(--ink-500)] mt-3 max-w-xl mx-auto">
          Apresente seu comprovante de agendamento nos parceiros abaixo e aproveite descontos e cortesias.
        </p>
      </div>

      {items.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-3 mb-8 md:flex-wrap md:justify-center" data-testid="partner-category-filters">
          <CatPill label="Todas" count={items.length} active={!active} onClick={() => pick("")} testid="cat-filter-all"/>
          {Object.entries(counts).map(([c, n]) => (
            <CatPill key={c} label={c} count={n} active={active === c} onClick={() => pick(c)} testid={`cat-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}/>
          ))}
        </div>
      )}

      {active && shown.length === 0 && (
        <p className="text-center text-sm text-[color:var(--ink-500)]" data-testid="partner-filter-empty">Nenhum parceiro nesta categoria.</p>
      )}

      <div className="grid md:grid-cols-3 gap-5">
        {shown.map(p => {
          const photo = photoOf(p);
          const insta = p.instagram_url || (/instagram\.com/i.test(p.image_url || "") ? p.image_url : "");
          return (
          <div key={p.partnership_id} className="card-arya overflow-hidden flex flex-col" data-testid={`partner-${p.partnership_id}`}>
            {photo && (
              <div className="aspect-video overflow-hidden bg-[var(--cream-100)]">
                <img src={mediaUrl(photo)} alt={p.name} className="w-full h-full object-cover" data-testid={`partner-img-${p.partnership_id}`}/>
              </div>
            )}
            <div className="p-6 flex-1 flex flex-col">
              <div className="flex items-center gap-2 mb-2"><Gift size={16} className="text-[color:var(--sun-600)]"/><span className="tag-sun">{p.category}</span></div>
              <h3 className="font-serif text-2xl">{p.name}</h3>
              <p className="text-sm text-[color:var(--ink-500)] mt-2 flex-1">{p.benefit}</p>
              {p.description && <p className="text-xs text-[color:var(--ink-500)] mt-2">{p.description}</p>}
              {insta && <a href={insta} target="_blank" rel="noreferrer" className="mt-3 text-xs inline-flex items-center gap-1 text-[color:var(--sun-700)] hover:underline" data-testid={`partner-insta-${p.partnership_id}`}><Instagram size={12}/> Ver Instagram</a>}
              <div className="mt-4 flex items-center justify-between rounded-xl border border-dashed border-[color:var(--sun-500)] p-3 bg-[rgba(233,204,133,.08)]">
                <span className="font-mono text-sm tracking-widest text-[color:var(--sun-700)]">{p.code}</span>
                <button onClick={() => copy(p.code)} className="btn-ghost !py-1 !px-3 text-xs inline-flex items-center gap-1" data-testid={`partner-copy-${p.partnership_id}`}><Copy size={12}/> Copiar</button>
              </div>
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
}

const CatPill = ({ label, count, active, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`shrink-0 whitespace-nowrap rounded-full px-4 py-2 text-sm border transition-colors duration-200 ${active
      ? "bg-[var(--sun-500)] border-[var(--sun-500)] text-white shadow-md"
      : "bg-white border-[var(--line)] text-[color:var(--ink-700)] hover:border-[var(--sun-500)]"}`}>
    {label} <span className={`ml-1 text-xs ${active ? "opacity-90" : "text-[color:var(--ink-500)]"}`}>({count})</span>
  </button>
);
